import React from 'react'
import PropTypes from 'prop-types'
import { MyButton } from '@interstellar-component'

import gridMask from '@src/assets/company-empty-state/grid-mask.svg'
import gridVertical from '@src/assets/company-empty-state/grid-vertical.svg'
import gridHorizontal from '@src/assets/company-empty-state/grid-horizontal.svg'
import illusBackgroundCircle from '@src/assets/company-empty-state/illus-background-circle.svg'
import illusDot12 from '@src/assets/company-empty-state/illus-dot-12.svg'
import illusDot16 from '@src/assets/company-empty-state/illus-dot-16.svg'
import illusDot10 from '@src/assets/company-empty-state/illus-dot-10.svg'
import illusCard from '@src/assets/company-empty-state/illus-card.svg'
import illusAlertCircle from '@src/assets/company-empty-state/illus-alert-circle.svg'

/**
 * Untitled UI "background pattern decorative" — the 480x480 grid that fades out
 * radially behind the illustration. The fade is a MASK, not a gradient overlay:
 * the card sits on a near-white surface, so painting a white gradient on top
 * would leave a visible square edge wherever the two whites disagree.
 *
 * Tailwind 3 has no mask utilities (they arrived in v4), hence the inline style.
 * Both the prefixed and unprefixed properties are set — WebKit still needs its.
 */
function GridPattern() {
  const mask = {
    maskImage: `url("${gridMask}")`,
    WebkitMaskImage: `url("${gridMask}")`,
    maskSize: '480px 480px',
    WebkitMaskSize: '480px 480px',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
  }

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-[-176px] size-[480px] -translate-x-1/2"
      aria-hidden
    >
      <div className="absolute left-0 top-0 size-[480px]" style={mask}>
        <img src={gridVertical} alt="" className="absolute inset-0 size-full max-w-none" />
        <img src={gridHorizontal} alt="" className="absolute inset-0 size-full max-w-none" />
      </div>
    </div>
  )
}

/**
 * The 172x128 illustration: a card, four scattered dots and the alert chip.
 *
 * Every piece is the asset exported from Figma, absolutely placed at its
 * designed offset. The card art is deliberately larger than its layout box
 * (178x126 drawn inside a 138x86 slot) because the drop shadow is baked into
 * the SVG — cropping it to the box would clip the shadow off.
 *
 * `tone` only repaints the alert chip. Brand purple is the designed colour and
 * reads as "nothing here yet"; a failed load has to read as "something broke",
 * and at this size the chip is the only element large enough to carry that
 * difference at a glance.
 */
function Illustration({ tone }) {
  const chip = tone === 'error' ? 'bg-[rgba(217,45,32,0.4)]' : 'bg-[rgba(105,65,198,0.4)]'

  return (
    <div className="relative h-[128px] w-[172px] shrink-0" aria-hidden>
      <img
        src={illusBackgroundCircle}
        alt=""
        className="absolute left-[22px] top-0 size-[128px] max-w-none"
      />
      <img src={illusDot12} alt="" className="absolute left-[14px] top-0 size-[12px] max-w-none" />
      <img
        src={illusDot16}
        alt=""
        className="absolute left-[9px] top-[104px] size-[16px] max-w-none"
      />
      <img
        src={illusDot16}
        alt=""
        className="absolute left-[152px] top-[28px] size-[16px] max-w-none"
      />
      <img
        src={illusDot10}
        alt=""
        className="absolute left-[144px] top-[4px] size-[10px] max-w-none"
      />

      <div className="absolute left-[17px] top-[16px] h-[86px] w-[138px]">
        <img
          src={illusCard}
          alt=""
          className="absolute left-[-20px] top-0 h-[126px] w-[178px] max-w-none"
        />
      </div>

      <div
        className={`absolute left-[58px] top-[78px] size-[56px] rounded-full backdrop-blur-[4px] ${chip}`}
      >
        <img
          src={illusAlertCircle}
          alt=""
          className="absolute left-[15.33px] top-[15.33px] size-[25.33px] max-w-none"
        />
      </div>
    </div>
  )
}

Illustration.propTypes = { tone: PropTypes.oneOf(['empty', 'error']) }
Illustration.defaultProps = { tone: 'empty' }

/**
 * The two answers this card must never blur together.
 *
 * A load that failed used to arrive here wearing the empty state's clothes —
 * same neutral card, same "belum tersedia" copy — which stated something about
 * the tenant's account when in fact only the network had gone wrong. So the
 * failure is a VARIANT, not just different strings: picking it also brings the
 * red chip, the error-tinted card and the retry button along with it, and no
 * caller can accidentally ship half of that.
 *
 * Loading is deliberately NOT a variant here. It is not an answer, it is the
 * absence of one, and the codebase already renders it with <MyLoadingData />
 * (see SaldoHistory) — giving it a card of its own would invite callers to
 * treat "still loading" as a settled state.
 */
const VARIANT_COPY = {
  empty: {
    title: 'Plan & saldo not available yet',
    description:
      'Your subscription plan and saldo become active once the credit bureau (CLIK) verification is complete. This usually takes 1–3 business days.',
  },
  error: {
    title: 'Failed to load data',
    description:
      'We could not fetch the data from the server. This does not mean there is no data — try reloading in a moment.',
  },
}

/**
 * "Plan & saldo not available yet" — what the Company information page shows in
 * place of the package and saldo cards while the company is not yet ACTIVE.
 *
 * It replaces those cards rather than joining them: there is no package to
 * price and no wallet to spend until CLIK validates the company, so rendering
 * them with zeroes would read as "you have a subscription worth Rp0" instead of
 * "this has not started yet".
 */
function EmptyState({ title, description, actions, variant, onRetry, retryLabel }) {
  const fallback = VARIANT_COPY[variant] || VARIANT_COPY.empty
  const isError = variant === 'error'
  // `??`, not `||`: a caller that deliberately passes '' wants no line there,
  // and must not be handed the variant's default copy instead.
  const heading = title ?? fallback.title
  const body = description ?? fallback.description
  const surface = isError ? 'border-error/200 bg-error/25' : 'border-gray/200 bg-gray/25'
  const hasActions = !!actions || !!onRetry

  return (
    <div
      className={`flex min-h-[260px] w-full flex-col items-center justify-center overflow-hidden rounded-xl border py-8 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] ${surface}`}
      role={isError ? 'alert' : undefined}
    >
      <div className="relative flex w-[512px] max-w-full items-center justify-center px-4">
        <GridPattern />
        {/* gap-8 between the copy block and the actions row is the Untitled UI
            empty-state spec — the Figma component leaves that second slot empty
            because the state it drew (waiting on CLIK) has nothing to act on. */}
        <div className="relative flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-5">
            <Illustration tone={isError ? 'error' : 'empty'} />
            <div className="flex max-w-[352px] flex-col items-center gap-2 text-center">
              <p className="text-lg-semibold text-gray/900">{heading}</p>
              <p className="text-sm-regular text-gray/600">{body}</p>
            </div>
          </div>
          {hasActions ? (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {actions}
              {onRetry ? (
                <MyButton
                  color={isError ? 'primary' : 'secondary'}
                  variant={isError ? 'filled' : 'outlined'}
                  size="md"
                  type="button"
                  onClick={onRetry}
                >
                  <span className={`text-sm-semibold ${isError ? 'text-white' : ''}`}>
                    {retryLabel}
                  </span>
                </MyButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

EmptyState.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  actions: PropTypes.node,
  // 'empty'  → this really is nothing (an answered request with no rows)
  // 'error'  → we do not know, because the request never got an answer
  variant: PropTypes.oneOf(['empty', 'error']),
  // Supplying it is what makes the failure recoverable without a full reload.
  onRetry: PropTypes.func,
  retryLabel: PropTypes.string,
}

// title/description default to null so the variant decides the copy. The
// resolved 'empty' strings are byte-for-byte the ones that used to live here,
// which is why the existing caller (CompanyInformation, which always passes
// both) is unaffected.
EmptyState.defaultProps = {
  title: null,
  description: null,
  actions: null,
  variant: 'empty',
  onRetry: null,
  retryLabel: 'Try again',
}

export default EmptyState
